import { createElement } from 'react';

import dotize from 'dotize';
import i18next from 'i18next';
import { isValidPhoneNumber } from 'libphonenumber-js';
import Papa from 'papaparse';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { getVariableNamesFromNamedParams } from '@/modules/whatsapp/utils';
import { WhatsappTemplate } from '@/shared/types/broadcast';
import {
  BaseCampaignSender,
  CampaignSender,
  CampaignSenderMetadata,
  DEFAULT_FIRST_MESSAGE_DELAY_MS,
  MAX_FIRST_MESSAGE_DELAY_MS,
  CampaignSubscriber,
  CampaignTransition,
  CampaignType,
  EmailTemplate,
  NBAIntegrationMappings,
  NewCampaignSender,
  NewCampaignSubscriber,
  PartialCampaignSender,
  SmsTemplate,
  TransitionCondition,
  VoiceCallSettings,
} from '@/shared/types/campaigns';
import { IntegrationType, TelnyxVoiceConfig, TwilioVoiceConfig } from '@/shared/types/integration';
import { Option } from '@/shared/types/rules';
import { isOlderThan } from '@/util/dateUtils';
import { noop } from '@/util/events';
import { sanitizeVariableName } from '@/util/strings';
import { emailPattern } from '@/util/validation/patterns';

import { getDebtCampaignVariables } from './constants';
import { DraftCampaignSubscriber, DraftCampaignSender } from './redux/actions';
import { ColumnMapping, VariableDefinition } from './types/column-mapping';
import { CSV_PARSE_OPTIONS, type CsvParseRequest, type CsvParseResponse } from './types/csv-parser';

// Every type except payment_recovery uses the debt-less outreach CSV shape.
export const isOutreachCampaign = (campaignType?: CampaignType): boolean =>
  campaignType !== 'payment_recovery';

/**
 * Customer CSVs sometimes contain placeholder tokens for missing data (the
 * literal text "null", "none", "n/a", "-", or just whitespace). Our ingest
 * stores them verbatim, so the UI sees a truthy string like "null" and prints
 * it. Treat these as missing when rendering subscriber context.
 */
const PLACEHOLDER_NULL_TOKENS = new Set(['', 'null', 'undefined', 'none', 'n/a', '-']);
export const cleanContextString = (value: unknown): string | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }
  const trimmed = value.trim();
  return PLACEHOLDER_NULL_TOKENS.has(trimmed.toLowerCase()) ? undefined : trimmed;
};

/**
 * The "required fields still unmatched" warning toast, shared by the upload path
 * and the Continue gate so its copy and styling stay in one place. `onMatchFields`
 * runs when the user clicks the "Match fields" action.
 */
export const showUnmatchedRequiredToast = (
  t: (key: string, options?: Record<string, unknown>) => string,
  onMatchFields: () => void
): void => {
  const toastId = toast.warning(t('campaigns.setup.unmatched_required_title'), {
    description: t('campaigns.setup.unmatched_required_description'),
    action: createElement(
      Button,
      {
        variant: 'primary',
        className: 'h-auto min-w-0 shrink-0 px-2 py-1',
        onClick: () => {
          toast.dismiss(toastId);
          onMatchFields();
        },
      },
      t('campaigns.setup.match_fields')
    ),
    classNames: { toast: '!w-128 !items-center !gap-4 !p-4', icon: '!mt-0.5 !self-start' },
  });
};

/**
 * Compute audience stats from transformed subscribers for draft campaigns.
 * Used when processing CSV uploads to calculate summary statistics.
 * Note: total_subscribers is NOT included - use campaign.subscribers_count as source of truth
 * @param subscribers - Array of draft campaign subscribers
 * @param headerCount - Number of columns/variables in the CSV
 * @returns Audience statistics for draft state
 */
export const computeAudienceStats = (
  subscribers: DraftCampaignSubscriber[],
  headerCount: number
) => ({
  email_subscribers: subscribers.filter((s) => s.email).length,
  phone_subscribers: subscribers.filter((s) => s.phone).length,
  // Sum across the legacy single-debt key (`debt.amount`) plus all slot-indexed
  // multi-debt keys (`debt.<N>.amount`). Slotted keys are what
  // `transformCsvWithMappings` emits after Orchestrator PR #1839.
  total_amount_due: subscribers.reduce((sum, s) => {
    let perRow = parseFloat(String(s['debt.amount'] ?? '')) || 0;
    for (const [key, value] of Object.entries(s)) {
      if (/^debt\.\d+\.amount$/.test(key)) {
        perRow += parseFloat(String(value)) || 0;
      }
    }
    return sum + perRow;
  }, 0),
  variables_count: headerCount,
});

export const MAX_REPLAY_AGE_MONTHS = 6;

export const getReplayability = (
  subscriber: CampaignSubscriber
): { sessionId: string | undefined; canReplay: boolean } => {
  const sessionId = subscriber.sender_history?.[0]?.session_id;
  const hasReplied = !!subscriber.last_replied_at;
  const withinReplayWindow = !isOlderThan(subscriber.updated, MAX_REPLAY_AGE_MONTHS);
  return { sessionId, canReplay: !!sessionId && hasReplied && withinReplayWindow };
};

/**
 * Empty sender configuration with default values
 * Used for initializing new senders in forms
 */
export const EMPTY_SENDER: BaseCampaignSender = {
  integrationId: '',
  integrationType: '' as IntegrationType,
  content: '',
  subject: '',
  template: undefined,
  headerParameters: [],
  bodyParameters: [],
  footerParameters: [],
  headerMediaParam: undefined,
};

/**
 * Default wait time for "no response" fallback senders (7 days in minutes)
 */
export const DEFAULT_NO_RESPONSE_WAIT_MINUTES = 7 * 24 * 60;

// ============================================================================
// CAMPAIGN SENDER CHAIN UTILITIES
// ============================================================================

/**
 * Result of parsing campaign senders into their chain structure
 */
export interface ParsedCampaignSendersChain<
  T extends { sender_id: string; transitions?: CampaignTransition[] },
> {
  /** The primary sender (has transitions pointing to fallbacks) */
  primarySender: T | undefined;
  /** The delivery_failed fallback sender (if any) */
  deliveryFailedSender: T | undefined;
  /** Array of no_response senders in chain order, with their wait times */
  noResponseSenders: Array<{ sender: T; afterMinutes: number }>;
}

/**
 * Parse campaign senders into their chain structure.
 *
 * The backend uses chained transitions for no_response senders:
 *   Primary → (no_response) → A → (no_response) → B → ...
 *
 * This function traverses the chain and returns:
 * - The primary sender (the one with transitions)
 * - The delivery_failed sender (if any)
 * - All no_response senders in order, with their wait times
 *
 * @param campaignSenders - Array of campaign senders from API
 * @returns Parsed chain structure
 */
export const parseCampaignSendersChain = <
  T extends { sender_id: string; transitions?: CampaignTransition[] },
>(
  campaignSenders: T[] | undefined
): ParsedCampaignSendersChain<T> => {
  const result: ParsedCampaignSendersChain<T> = {
    primarySender: undefined,
    deliveryFailedSender: undefined,
    noResponseSenders: [],
  };

  if (!campaignSenders || campaignSenders.length === 0) {
    return result;
  }

  // Collect all sender IDs that are targets of transitions
  // These are fallback senders, not the primary
  const targetSenderIds = new Set<string>();
  for (const sender of campaignSenders) {
    if (sender.transitions) {
      for (const transition of sender.transitions) {
        if (transition.sender_id) {
          targetSenderIds.add(transition.sender_id);
        }
      }
    }
  }

  // Primary sender is the one with transitions that is NOT a target of any transition
  // This correctly handles chained senders where A → B → C (A is primary, B and C are targets)
  result.primarySender =
    campaignSenders.find(
      (sender) =>
        sender.transitions &&
        sender.transitions.length > 0 &&
        !targetSenderIds.has(sender.sender_id)
    ) || campaignSenders[0]; // Fallback to first if no clear primary

  if (!result.primarySender) {
    return result;
  }

  // Find delivery_failed sender from primary's transitions
  const deliveryFailedTransition = result.primarySender.transitions?.find(
    (t) => t.condition === TransitionCondition.DELIVERY_FAILED
  );
  if (deliveryFailedTransition?.sender_id) {
    result.deliveryFailedSender = campaignSenders.find(
      (s) => s.sender_id === deliveryFailedTransition.sender_id
    );
  }

  // Traverse the no_response chain starting from primary
  // Chain structure: Primary → A → B → C (each sender points to the next)
  let currentTransition = result.primarySender.transitions?.find(
    (t) => t.condition === TransitionCondition.NO_RESPONSE
  );

  while (currentTransition?.sender_id) {
    const nextSender = campaignSenders.find((s) => s.sender_id === currentTransition!.sender_id);
    if (!nextSender) {
      break; // Broken chain - stop traversal
    }

    result.noResponseSenders.push({
      sender: nextSender,
      afterMinutes: currentTransition.after_minutes || 0,
    });

    // Follow the chain - look for this sender's no_response transition
    currentTransition = nextSender.transitions?.find(
      (t) => t.condition === TransitionCondition.NO_RESPONSE
    );
  }

  return result;
};

/**
 * Calculate audience statistics from subscribers data
 * Excludes subscribers with errors from the counts
 * @param subscribers - Array of draft campaign subscribers
 * @param campaignSubscribersCount - Total subscribers count from campaign object (for existing campaigns)
 * @returns Audience statistics object with counts
 */
export const getAudienceStats = (
  subscribers: DraftCampaignSubscriber[],
  campaignSubscribersCount?: number
) => {
  if (!subscribers.length && !campaignSubscribersCount) {
    return null;
  }

  let totalCount = 0;
  let emailCount = 0;
  let phoneCount = 0;
  const variableSet = new Set<string>();

  // Count valid (non-error) subscribers
  const validSubscribers = subscribers.filter((sub) => !sub.error);

  validSubscribers.forEach((subscriber) => {
    // Count valid subscribers
    totalCount++;

    // Count emails
    if (subscriber.email && subscriber.email.trim()) {
      emailCount++;
    }

    // Count phone numbers
    if (subscriber.phone && subscriber.phone.trim()) {
      phoneCount++;
    }

    // Collect unique variable names (excluding email, phone, and error)
    Object.keys(subscriber).forEach((key) => {
      if (
        key !== 'email' &&
        key !== 'phone' &&
        key !== 'error' &&
        subscriber[key] &&
        subscriber[key]?.trim()
      ) {
        variableSet.add(key);
      }
    });
  });

  // If we have a campaignSubscribersCount (existing campaign with pagination)
  // Check if all subscribers in the sample have email/phone, and extrapolate
  if (campaignSubscribersCount) {
    totalCount = campaignSubscribersCount;

    // Only extrapolate email/phone counts if we have sample data
    if (validSubscribers.length > 0) {
      const allHaveEmail = validSubscribers.every((sub) => sub.email && sub.email.trim());
      const allHavePhone = validSubscribers.every((sub) => sub.phone && sub.phone.trim());

      // If all subscribers in the sample have email, assume all do
      if (allHaveEmail) {
        emailCount = campaignSubscribersCount;
      }

      // If all subscribers in the sample have phone, assume all do
      if (allHavePhone) {
        phoneCount = campaignSubscribersCount;
      }
    }
  }

  return {
    total_subscribers: totalCount,
    email_subscribers: emailCount,
    phone_subscribers: phoneCount,
    variables_count: variableSet.size,
  };
};

/**
 * Audience stats from saved API subscribers — flattens each subscriber's
 * context into the draft (flat) shape so getAudienceStats can count variables.
 */
export const getApiSubscribersStats = (
  apiSubscribers: CampaignSubscriber[],
  campaignSubscribersCount?: number
) => {
  const draftFormatSubscribers: DraftCampaignSubscriber[] = apiSubscribers.map((sub) => {
    const draft: DraftCampaignSubscriber = {};
    if (sub.email) {
      draft.email = String(sub.email);
    }
    if (sub.phone) {
      draft.phone = String(sub.phone);
    }
    if (sub.context && typeof sub.context === 'object') {
      const flatContext = dotize.convert(sub.context);
      Object.entries(flatContext).forEach(([key, value]) => {
        if (value) {
          draft[key] = String(value);
        }
      });
    }
    return draft;
  });
  return getAudienceStats(draftFormatSubscribers, campaignSubscribersCount);
};

/**
 * Generate context variable options from campaign subscribers
 * Extracts variables from the nested context object and flattens them using dot notation
 * Separates variables into two groups:
 * - "complete_variables": Variables that ALL subscribers have (non-empty values)
 * - "incomplete_variables": Variables that at least one subscriber is missing or has empty
 * @param subscribers - Array of campaign subscribers from API
 * @returns Array of variable options for autocomplete with type indicating completeness
 */
export const getCampaignContextVariableOptions = (subscribers: CampaignSubscriber[]): Option[] => {
  if (!subscribers || subscribers.length === 0) {
    return [];
  }

  const variableSet = new Set<string>();

  // Collect all unique variable names from context objects
  subscribers.forEach((subscriber) => {
    if (subscriber.context && typeof subscriber.context === 'object') {
      const flattenedContext = dotize.convert(subscriber.context);
      Object.keys(flattenedContext).forEach((key) => {
        const valueType = typeof flattenedContext[key];
        if (valueType === 'string' || valueType === 'number') {
          variableSet.add(key);
        }
      });
    }
  });

  // For each variable, check if ALL subscribers have a non-empty value
  const options: Option[] = Array.from(variableSet).map((key) => {
    const allSubscribersHaveValue = subscribers.every((subscriber) => {
      if (!subscriber.context) {
        return false;
      }

      const flattenedContext = dotize.convert(subscriber.context);
      const value = flattenedContext[key];

      return (
        value !== undefined &&
        value !== null &&
        (typeof value === 'number' || (typeof value === 'string' && value.trim() !== ''))
      );
    });

    return {
      label: key,
      value: `{{$${key}}}`,
      type: allSubscribersHaveValue ? 'available_variables' : 'partial_variables',
    };
  });

  // Sort options by type so available_variables appear first, then by label alphabetically
  return options.sort((a, b) => {
    // First sort by type (available_variables before partial_variables)
    if (a.type !== b.type) {
      return a.type === 'available_variables' ? -1 : 1;
    }
    // Then sort alphabetically by label within each type
    return a.label.localeCompare(b.label);
  });
};

/** Keys on DraftCampaignSubscriber that are not context variables */
const DRAFT_SUBSCRIBER_RESERVED_KEYS = new Set(['email', 'phone', 'error']);

/**
 * Extract context variable options from draft (in-Redux) subscribers.
 * Draft subscribers are flat objects where every key except email/phone/error is a variable.
 */
export const getDraftContextVariableOptions = (
  subscribers: DraftCampaignSubscriber[]
): Option[] => {
  if (!subscribers || subscribers.length === 0) {
    return [];
  }

  const variableSet = new Set<string>();

  subscribers.forEach((subscriber) => {
    Object.keys(subscriber).forEach((key) => {
      if (!DRAFT_SUBSCRIBER_RESERVED_KEYS.has(key) && typeof subscriber[key] === 'string') {
        variableSet.add(key);
      }
    });
  });

  const options: Option[] = Array.from(variableSet).map((key) => {
    const allSubscribersHaveValue = subscribers.every((subscriber) => {
      const value = subscriber[key];
      return value !== undefined && value !== null && value.trim() !== '';
    });

    return {
      label: key,
      value: `{{$${key}}}`,
      type: allSubscribersHaveValue ? 'available_variables' : 'partial_variables',
    };
  });

  return options.sort((a, b) => {
    if (a.type !== b.type) {
      return a.type === 'available_variables' ? -1 : 1;
    }
    return a.label.localeCompare(b.label);
  });
};

/**
 * Validation result for CSV file processing
 */
export interface CsvValidationResult {
  isValid: boolean;
  error?: string;
  subscribers?: DraftCampaignSubscriber[];
  headers?: string[];
  rows?: string[][];
}

/**
 * Validate if file is a valid CSV file
 */
export const validateCsvFile = (file: File): { isValid: boolean; error?: string } => {
  if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
    return {
      isValid: false,
      error: i18next.t('file_upload.csv.invalid_file_type') || 'file_upload.csv.invalid_file_type',
    };
  }
  return { isValid: true };
};

/**
 * Format file data from CSV headers and rows.
 * Maps phone/email to root level; everything else lands in context under a
 * `sanitizeVariableName`d key so non-variable-safe headers (spaces, punctuation,
 * casing) still get ingested — e.g. "First Name" → `first_name`.
 */
export const formatFileSubscribers = (headers: string[], data: string[][]) => {
  const subscribers: DraftCampaignSubscriber[] = [];
  let invalidFile = false;

  // Check if we have required headers (email or phone)
  const hasEmail = headers.some((h) => h.toLowerCase().includes('email'));
  const hasPhone = headers.some((h) => h.toLowerCase().includes('phone'));

  if (!hasEmail && !hasPhone) {
    invalidFile = true;
    return { subscribers: [], invalidFile };
  }

  const keys = headers.map((header) => {
    const headerLower = header.toLowerCase();
    // Keep phone and email at root level
    if (headerLower === 'phone' || headerLower === 'email') {
      return headerLower;
    }
    return sanitizeVariableName(header).toLowerCase();
  });

  // Process each row (skip header row)
  for (let i = 1; i < data.length; i++) {
    const row = data[i];
    if (row && row.length > 0) {
      const subscriber: DraftCampaignSubscriber = {};

      keys.forEach((key, index) => {
        const value = row[index]?.trim();
        if (key && value) {
          subscriber[key] = value;
        }
      });

      subscribers.push(subscriber);
    }
  }

  return { subscribers, invalidFile };
};

const validateParsedRows = (
  data: string[][],
  onSuccess: (result: CsvValidationResult) => void,
  onError: (error: string) => void,
  options?: { skipRequiredColumnValidation?: boolean }
): void => {
  if (data.length < 2) {
    onError(
      i18next.t('file_upload.csv.missing_header_row') || 'file_upload.csv.missing_header_row'
    );
    return;
  }

  const headers = data[0];

  // Check for duplicate headers - these would cause mapping collisions
  const headerCounts = new Map<string, number>();
  const duplicates: string[] = [];
  for (const header of headers) {
    const normalized = header?.trim().toLowerCase() || '';
    const count = (headerCounts.get(normalized) || 0) + 1;
    headerCounts.set(normalized, count);
    if (count === 2 && normalized) {
      duplicates.push(header);
    }
  }
  if (duplicates.length > 0) {
    onError(
      i18next.t('file_upload.csv.duplicate_headers', { 0: duplicates.join(', ') }) ||
        `Duplicate column headers found: ${duplicates.join(', ')}`
    );
    return;
  }

  const { subscribers, invalidFile } = formatFileSubscribers(headers, data);

  // Skip required column validation if column mapping modal will be shown
  if (invalidFile && !options?.skipRequiredColumnValidation) {
    onError(
      i18next.t('file_upload.csv.missing_required_columns') ||
        'file_upload.csv.missing_required_columns'
    );
    return;
  }

  // Get data rows (skip header row)
  const dataRows = data.slice(1);

  // Filter out empty columns (empty header names) to avoid clutter in the mapping UI
  const nonEmptyIndices = headers.map((h, i) => (h?.trim() ? i : -1)).filter((i) => i !== -1);
  const filteredHeaders = nonEmptyIndices.map((i) => headers[i]);
  const filteredRows = dataRows.map((row) => nonEmptyIndices.map((i) => row[i]));

  // Success - include headers and rows for column mapping modal
  onSuccess({ isValid: true, subscribers, headers: filteredHeaders, rows: filteredRows });
};

/**
 * Parse CSV file and return validation result with subscribers data.
 *
 * Tokenizing runs in `csvParser.worker.ts`; a worker that cannot start parses on
 * the main thread instead. Never set Papa Parse's own `worker` option: that one
 * runs as a sloppy-mode blob, where our minified bundle shadows the config and
 * `skipEmptyLines` throws.
 *
 * @param file - CSV file to parse
 * @param onSuccess - Callback on successful parse
 * @param onError - Callback on error
 * @param options - Parse options
 * @param options.skipRequiredColumnValidation - Skip email/phone column validation (use when column mapping modal will be shown)
 * @returns Cancels the parse: the worker is terminated and no callback fires
 */
export const parseCsvFile = (
  file: File,
  onSuccess: (result: CsvValidationResult) => void,
  onError: (error: string) => void,
  options?: { skipRequiredColumnValidation?: boolean }
): (() => void) => {
  // First validate the file type
  const fileValidation = validateCsvFile(file);
  if (!fileValidation.isValid) {
    onError(fileValidation.error!);
    return noop;
  }

  let worker: Worker | undefined;

  let settled = false;
  const settle = (finish: () => void) => {
    if (settled) {
      return;
    }
    settled = true;
    worker?.terminate();
    finish();
  };

  const reportError = (message: string) => {
    onError(
      i18next.t('file_upload.csv.parse_error', { 0: message }) || 'file_upload.csv.parse_error'
    );
  };

  const parseOnMainThread = () => {
    Papa.parse<string[]>(file, {
      ...CSV_PARSE_OPTIONS,
      complete: (result) =>
        settle(() => validateParsedRows(result.data, onSuccess, onError, options)),
      error: (error: Error) => settle(() => reportError(error.message)),
    });
  };

  try {
    worker = new Worker(new URL('./csvParser.worker.ts', import.meta.url), { type: 'module' });
  } catch {
    parseOnMainThread();
    return () => settle(noop);
  }

  worker.onmessage = ({ data: response }: MessageEvent<CsvParseResponse>) =>
    settle(() => {
      if (response?.type === 'rows' && Array.isArray(response.rows)) {
        validateParsedRows(response.rows, onSuccess, onError, options);
        return;
      }
      reportError(response?.type === 'error' ? response.message : 'Unexpected parser response');
    });

  worker.onerror = () => {
    if (settled) {
      return;
    }
    worker?.terminate();
    parseOnMainThread();
  };

  worker.postMessage({ file } satisfies CsvParseRequest);

  return () => settle(noop);
};

/**
 * Parse a currency string to a plain number string.
 * Handles both US (1,200.50) and European (1.200,50) formats.
 */
export const parseCurrencyToNumber = (value: string): string => {
  // Strip currency symbols and whitespace
  let cleaned = value.replace(/[^0-9.,]/g, '');

  if (!cleaned) {
    return value;
  }

  const lastComma = cleaned.lastIndexOf(',');
  const lastDot = cleaned.lastIndexOf('.');
  const digitsAfterLastComma = lastComma >= 0 ? cleaned.length - lastComma - 1 : -1;
  const digitsAfterLastDot = lastDot >= 0 ? cleaned.length - lastDot - 1 : -1;

  if (lastComma >= 0 && lastDot >= 0) {
    // Both separators present: last one is the decimal separator
    if (lastComma > lastDot) {
      cleaned = cleaned.replace(/\./g, '').replace(',', '.');
    } else {
      cleaned = cleaned.replace(/,/g, '');
    }
  } else if (lastComma >= 0) {
    // Only comma: 3 digits after = thousands separator, otherwise = decimal
    if (digitsAfterLastComma === 3) {
      cleaned = cleaned.replace(/,/g, '');
    } else {
      cleaned = cleaned.replace(',', '.');
    }
  } else if (lastDot >= 0 && digitsAfterLastDot === 3 && lastDot > 0) {
    // Only dot with exactly 3 digits after and digits before: thousands separator (e.g., "1.200")
    cleaned = cleaned.replace(/\./g, '');
  }

  return cleaned;
};

/**
 * Transform CSV data using column mappings
 * Applies the user-confirmed mappings to convert CSV columns to system variable names.
 *
 * @param headers - CSV column headers
 * @param dataRows - CSV data rows (excluding header row)
 * @param mappings - Column mappings from AI suggestions or user modifications
 * @param liveInstructionTargets - Optional. Emit paths the user marked for
 *   mirroring under `context.live_instructions.*`. Each entry should be a
 *   key the transformer would normally write to the subscriber (e.g. `email`,
 *   `country_code`, `debt.0.amount`, `promo_code`). Multi targets like
 *   `alternate_phones` are matched by bucket name.
 * @returns Array of draft subscribers with mapped data
 *
 * @example
 * ```ts
 * const mappings = {
 *   'Customer Phone': { target: 'phone' },
 *   'Full Name': { target: 'user.display_name' },
 *   'Internal ID': { target: null }, // Skip this column
 * };
 * const subscribers = transformCsvWithMappings(headers, dataRows, mappings);
 * ```
 */
export const transformCsvWithMappings = (
  headers: string[],
  dataRows: string[][],
  mappings: Record<string, ColumnMapping>,
  liveInstructionTargets?: string[]
): DraftCampaignSubscriber[] => {
  const subscribers: DraftCampaignSubscriber[] = [];

  // Pre-compute the sets of `multi` and `slotted` targets.
  // - `multi`: many CSV columns collected into one array under a single target
  //   (alternate_phones / alternate_emails). Indexed by encounter order.
  // - `slotted`: many debt blocks of the same shape addressed by
  //   `mapping.debt_index` (debts[0], debts[1], ...).
  const multiTargets = new Set<string>();
  const slottedTargets = new Set<string>();
  for (const v of getDebtCampaignVariables()) {
    if (v.multi) {
      multiTargets.add(v.name);
    }
    if (v.slotted) {
      slottedTargets.add(v.name);
    }
  }

  // Primary email / phone resolution: when multiple CSV columns map to the
  // same identity target, one becomes the top-level `email` / `phone` and the
  // rest spill into `alternate_emails` / `alternate_phones`. The modal can
  // mark a mapping as `primary: true`; otherwise the first encountered wins.
  const identityOverflow: Record<string, string> = {
    email: 'alternate_emails',
    phone: 'alternate_phones',
  };
  const primaryHeaderByTarget = new Map<string, string>();
  for (const target of Object.keys(identityOverflow)) {
    const headersForTarget = headers.filter((h) => mappings[h]?.target === target);
    if (headersForTarget.length === 0) {
      continue;
    }
    const userPicked = headersForTarget.find((h) => mappings[h]?.primary === true);
    primaryHeaderByTarget.set(target, userPicked ?? headersForTarget[0]);
  }

  for (const row of dataRows) {
    if (!row || row.length === 0) {
      continue;
    }

    const subscriber: DraftCampaignSubscriber = {};
    const altCounters = new Map<string, number>();

    headers.forEach((header, index) => {
      const mapping = mappings[header];
      const value = row[index]?.trim();

      if (!value) {
        return;
      }

      // Use mapped target name, or fall back to the raw header path.
      // For the fallback we preserve `name[N]` as `name.N` and sanitize each
      // dotted segment independently so array indices survive into
      // `setNestedValue` (which treats numeric segments as array indices).
      // This is what merges `debts[0].case_id` into `context.debts[0]`
      // alongside the canonical fields, instead of stashing it under a
      // separate `context.debts_0_` object.
      const target =
        mapping?.target ??
        header
          .replace(/\[(\d+)\]/g, '.$1')
          .split('.')
          .map((seg) => (/^\d+$/.test(seg) ? seg : sanitizeVariableName(seg).toLowerCase()))
          .filter((seg) => seg.length > 0)
          .join('.');
      if (!target) {
        return;
      }

      // Identity targets (email/phone): primary writes top-level; others
      // overflow into the matching alternate_* bucket as slot-indexed keys.
      if (target in identityOverflow) {
        const isPrimary = primaryHeaderByTarget.get(target) === header;
        if (isPrimary) {
          subscriber[target] = value;
          return;
        }
        const altKey = identityOverflow[target];
        const next = altCounters.get(altKey) ?? 0;
        subscriber[`${altKey}.${next}`] = value;
        altCounters.set(altKey, next + 1);
        return;
      }

      // `multi: true` targets: collect values into a slot-indexed bucket in
      // encounter order. Downstream serializer compacts into an array.
      if (multiTargets.has(target)) {
        const next = altCounters.get(target) ?? 0;
        subscriber[`${target}.${next}`] = value;
        altCounters.set(target, next + 1);
        return;
      }

      // `slotted: true` targets: emit `<groupKey>.<index>.<field>` so the
      // serializer can build `context.debts[index].field`. Index defaults to 0.
      if (slottedTargets.has(target)) {
        const debtIndex = mapping?.debt_index ?? 0;
        const dotIdx = target.indexOf('.');
        if (dotIdx <= 0) {
          subscriber[target] = value;
          return;
        }
        const groupKey = target.slice(0, dotIdx);
        const fieldKey = target.slice(dotIdx + 1);
        subscriber[`${groupKey}.${debtIndex}.${fieldKey}`] = value;
        return;
      }

      subscriber[target] = value;
    });

    // Normalize top-level + alternate phones to E.164 format.
    if (subscriber.phone && !String(subscriber.phone).startsWith('+')) {
      subscriber.phone = `+${subscriber.phone}`;
    }
    for (const key of Object.keys(subscriber)) {
      if (/^alternate_phones\.\d+$/.test(key)) {
        const v = String(subscriber[key]);
        if (v && !v.startsWith('+')) {
          subscriber[key] = `+${v}`;
        }
      }
    }

    // Parse currency for every debt slot's amount: `debt.<N>.amount`.
    for (const key of Object.keys(subscriber)) {
      if (/^debt\.\d+\.amount$/.test(key) && subscriber[key]) {
        subscriber[key] = parseCurrencyToNumber(String(subscriber[key]));
      }
    }

    // Mirror selected subscriber values under `live_instructions.*` so the
    // LLM can read them straight out of `live_instructions` without needing
    // to traverse the wider context tree (MOV-15472). The rename below maps
    // singular `debt.<N>.<field>` → plural `debts.<N>.<field>` so
    // `context.live_instructions` mirrors the canonical `context.debts[]`
    // shape (the same fix `setNestedValue` applies at the top level).
    if (liveInstructionTargets && liveInstructionTargets.length > 0) {
      const liveSet = new Set(liveInstructionTargets);
      const mirrorKeyFor = (key: string): string => {
        if (key.startsWith('debt.')) {
          return `debts.${key.slice('debt.'.length)}`;
        }
        return key;
      };
      const shouldMirror = (key: string): boolean => {
        if (liveSet.has(key)) {
          return true;
        }
        const dot = key.indexOf('.');
        if (dot > 0) {
          const bucket = key.slice(0, dot);
          if (multiTargets.has(bucket) && liveSet.has(bucket)) {
            return true;
          }
        }
        return false;
      };
      // Snapshot keys before we start writing — otherwise we'd mirror our
      // own `live_instructions.*` writes back into themselves.
      for (const key of Object.keys(subscriber)) {
        if (key === 'error' || key === 'errorField') {
          continue;
        }
        if (key.startsWith('live_instructions.')) {
          continue;
        }
        if (!shouldMirror(key)) {
          continue;
        }
        subscriber[`live_instructions.${mirrorKeyFor(key)}`] = subscriber[key];
      }
    }

    // Only add subscriber if it has at least phone or email
    if (subscriber.phone || subscriber.email) {
      subscribers.push(subscriber);
    }
  }

  return subscribers;
};

/** Reconstructed CSV view of saved subscribers, fed back into the matcher. */
export interface ReconstructedCsv {
  headers: string[];
  rows: string[][];
  mappings: Record<string, ColumnMapping>;
}

/**
 * Map a reconstructed flat header back to its canonical mapping target.
 * `debt.<N>.<field>` → slotted `debt.<field>` at slot N; alternate buckets
 * collapse to their multi target; everything else maps to itself (identity).
 */
const reconstructHeaderMapping = (header: string): ColumnMapping => {
  const slotted = header.match(/^debt\.(\d+)\.(.+)$/);
  if (slotted) {
    return { target: `debt.${slotted[2]}`, debt_index: parseInt(slotted[1], 10) };
  }
  const alt = header.match(/^(alternate_phones|alternate_emails)\.\d+$/);
  if (alt) {
    return { target: alt[1] };
  }
  return { target: header };
};

/**
 * Rebuild a CSV-shaped view (`headers` + `rows` + identity `mappings`) from
 * already-saved subscribers so the Match Fields modal can reopen after a page
 * refresh, when the original upload (held only in Redux) is gone.
 *
 * The raw CSV isn't persisted, so we reconstruct from the canonical
 * `context.*` shape the API returns. Values are emitted verbatim — crucially
 * `due_date` stays the raw ms timestamp string, which `parseDateToTimestamp`
 * accepts unchanged, so re-confirming the modal round-trips losslessly back to
 * the same `context.debts[]`. Each header maps to itself (with the debt slot
 * for slotted fields), so nothing is silently re-targeted.
 *
 * @returns the reconstructed view, or `null` when there's nothing to rebuild.
 */
export const reconstructCsvFromSubscribers = (
  subscribers: CampaignSubscriber[] | undefined
): ReconstructedCsv | null => {
  if (!subscribers || subscribers.length === 0) {
    return null;
  }

  // Flatten one API subscriber into the flat-key shape the transformer emits.
  const flatten = (sub: CampaignSubscriber): Record<string, string> => {
    const flat: Record<string, string> = {};
    const put = (key: string, value: unknown) => {
      if (value !== undefined && value !== null && value !== '') {
        flat[key] = String(value);
      }
    };

    put('email', sub.email);
    put('phone', sub.phone);

    const ctx = (sub.context ?? {}) as Record<string, unknown>;
    put('country_code', ctx.country_code);
    put('residence_state', ctx.residence_state);
    put('account_id', ctx.account_id);
    const user = ctx.user as Record<string, unknown> | undefined;
    if (user) {
      put('user.display_name', user.display_name);
    }

    const debts = Array.isArray(ctx.debts) ? ctx.debts : [];
    debts.forEach((debt, i) => {
      const d = (debt ?? {}) as Record<string, unknown>;
      put(`debt.${i}.amount`, d.amount);
      put(`debt.${i}.currency`, d.currency);
      put(`debt.${i}.due_date`, d.due_date);
      put(`debt.${i}.original_creditor`, d.original_creditor);
      put(`debt.${i}.debt_external_id`, d.debt_external_id);
      put(`debt.${i}.debt_type`, d.debt_type);
    });

    const altPhones = Array.isArray(ctx.alternate_phones) ? ctx.alternate_phones : [];
    altPhones.forEach((p, i) => put(`alternate_phones.${i}`, p));
    const altEmails = Array.isArray(ctx.alternate_emails) ? ctx.alternate_emails : [];
    altEmails.forEach((e, i) => put(`alternate_emails.${i}`, e));

    return flat;
  };

  const flatRows = subscribers.map(flatten);

  // Header union across all rows, preserving first-seen order.
  const headers: string[] = [];
  const seen = new Set<string>();
  for (const row of flatRows) {
    for (const key of Object.keys(row)) {
      if (!seen.has(key)) {
        seen.add(key);
        headers.push(key);
      }
    }
  }
  if (headers.length === 0) {
    return null;
  }

  const rows = flatRows.map((row) => headers.map((h) => row[h] ?? ''));
  const mappings: Record<string, ColumnMapping> = {};
  for (const header of headers) {
    mappings[header] = reconstructHeaderMapping(header);
  }

  return { headers, rows, mappings };
};

/** ISO 3166-1 alpha-2 country code pattern (2 uppercase letters) */
const ISO_COUNTRY_CODE_PATTERN = /^[A-Z]{2}$/;

/** ISO 4217 currency code pattern (3 uppercase letters) */
const ISO_CURRENCY_PATTERN = /^[A-Z]{3}$/;

/** Residence state pattern (2 uppercase letters) — matches orchestrator schema */
const RESIDENCE_STATE_PATTERN = /^[A-Z]{2}$/;

/** Countries where residence_state is required (FDCPA compliance) */
const COUNTRIES_REQUIRING_STATE = new Set(['US', 'BR']);

/**
 * True when a subscriber's country mandates `residence_state` (FDCPA — US/BR).
 * Accepts any input shape so callers can hand it a Redux cell, a CSV string,
 * a number, null, etc. Centralizes the rule so the regex doesn't drift across
 * Setups, Audience pages, the matcher, and the sidebar.
 */
export const requiresResidenceState = (country: unknown): boolean => {
  if (typeof country !== 'string') {
    return false;
  }
  const normalized = country.trim().toUpperCase();
  return COUNTRIES_REQUIRING_STATE.has(normalized);
};

/** True when any subscriber's country (root or context) triggers the residence_state rule. */
export const audienceNeedsResidenceState = (subscribers: readonly unknown[]): boolean =>
  subscribers.some((sub) => {
    const country =
      (sub as { country_code?: unknown }).country_code ??
      (sub as { context?: { country_code?: unknown } }).context?.country_code;
    return requiresResidenceState(country);
  });

/** Conditionally-required variable names for this audience (residence_state on US/BR). */
export const getExtraRequiredFields = (subscribers: readonly unknown[]): string[] =>
  audienceNeedsResidenceState(subscribers) ? ['residence_state'] : [];

/** Valid ISO-4217 currency cell (same rule validateSubscriberFields enforces). */
export const isValidDebtCurrencyCell = (value: string): boolean =>
  ISO_CURRENCY_PATTERN.test(value.trim().toUpperCase());

/** Valid positive amount cell, accepting human-readable shapes ("$760", "1.200,50"). */
export const isValidDebtAmountCell = (value: string): boolean => {
  const amount = parseFloat(parseCurrencyToNumber(value));
  return !isNaN(amount) && amount > 0;
};

/**
 * Validate subscriber field values after CSV transform.
 * Attaches `error` and `errorField` to subscribers with invalid values.
 * Runs before the orchestrator verify call to catch format issues early.
 */
export const validateSubscriberFields = (
  subscribers: DraftCampaignSubscriber[],
  t: (key: string, options?: Record<string, unknown>) => string
): DraftCampaignSubscriber[] => {
  return subscribers.map((sub) => {
    // Skip already-errored subscribers
    if (sub.error) {
      return sub;
    }

    const normalizations: Partial<DraftCampaignSubscriber> = {};
    const issues: { message: string; field: string }[] = [];

    // country_code: must be ISO 3166-1 alpha-2 (e.g., "US", "BR")
    const countryCode = sub.country_code ? String(sub.country_code).trim().toUpperCase() : '';
    if (sub.country_code) {
      if (!ISO_COUNTRY_CODE_PATTERN.test(countryCode)) {
        issues.push({
          message: t('campaigns.validation.invalid_country_code', { 0: sub.country_code }),
          field: 'country_code',
        });
      } else {
        normalizations.country_code = countryCode;
      }
    }

    // residence_state: required for US/BR, must be 2 uppercase letters
    if (COUNTRIES_REQUIRING_STATE.has(countryCode) && !sub.residence_state) {
      issues.push({
        message: t('campaigns.validation.missing_residence_state', { 0: countryCode }),
        field: 'residence_state',
      });
    }
    if (sub.residence_state) {
      const state = String(sub.residence_state).trim().toUpperCase();
      if (!RESIDENCE_STATE_PATTERN.test(state)) {
        issues.push({
          message: t('campaigns.validation.invalid_residence_state', { 0: sub.residence_state }),
          field: 'residence_state',
        });
      } else {
        normalizations.residence_state = state;
      }
    }

    // debt.currency: must be ISO 4217 (e.g., "USD", "EUR")
    if (sub['debt.currency']) {
      const currency = String(sub['debt.currency']).trim().toUpperCase();
      if (!isValidDebtCurrencyCell(currency)) {
        issues.push({
          message: t('campaigns.validation.invalid_currency', { 0: sub['debt.currency'] }),
          field: 'debt.currency',
        });
      } else {
        normalizations['debt.currency'] = currency;
      }
    }

    // debt.amount: must be a valid positive number.
    // Strip currency symbols / thousands separators first so outreach
    // campaigns — which skip the column-mapping modal where this
    // normalization usually happens via transformCsvWithMappings — accept
    // the same human-readable shapes as payment-recovery uploads
    // (e.g. "$760", "1.200,50").
    if (sub['debt.amount']) {
      const cleaned = parseCurrencyToNumber(String(sub['debt.amount']));
      if (!isValidDebtAmountCell(String(sub['debt.amount']))) {
        issues.push({
          message: t('campaigns.validation.invalid_amount', { 0: sub['debt.amount'] }),
          field: 'debt.amount',
        });
      } else if (cleaned !== String(sub['debt.amount'])) {
        normalizations['debt.amount'] = cleaned;
      }
    }

    if (sub.email) {
      const email = String(sub.email).trim();
      if (!emailPattern.test(email)) {
        issues.push({
          message: t('campaigns.validation.invalid_email', { 0: sub.email }),
          field: 'email',
        });
      }
    }

    if (sub.phone) {
      const phone = String(sub.phone).trim();
      if (!isValidPhoneNumber(phone)) {
        issues.push({
          message: t('campaigns.validation.invalid_phone', { 0: sub.phone }),
          field: 'phone',
        });
      }
    }

    if (issues.length > 0) {
      return {
        ...sub,
        error: issues.map((i) => i.message).join('; '),
        errorField: issues.map((i) => i.field).join(','),
      };
    }

    // Return a new object with normalizations applied (never mutate the input)
    return Object.keys(normalizations).length > 0 ? { ...sub, ...normalizations } : sub;
  });
};

// ============================================================================
// CAMPAIGN TRANSFORMATION UTILITIES
// ============================================================================

/**
 * Campaign form interface with unified sender types
 * Note: Parameters are now per-sender, not shared at form level
 */
export interface CampaignForm {
  primarySender: BaseCampaignSender;
  fallbackSenders: BaseCampaignSender[];
  fallbackIfNotDelivered: boolean;
  fallbackIfNoResponse: boolean;
  // Campaign-level opener settings, written to every voice sender
  voiceSettings?: VoiceCallSettings;
}

/**
 * Transform template and parameters into broadcast-style components
 * @param template - WhatsApp template object
 * @param headerParameters - Header parameter values
 * @param bodyParameters - Body parameter values
 * @param footerParameters - Footer parameter values
 * @param headerMediaParam - Media header parameter (for IMAGE, VIDEO, DOCUMENT)
 * @returns Formatted template with components
 */
export const formatTemplateComponents = (
  template: WhatsappTemplate | EmailTemplate | SmsTemplate | undefined,
  headerParameters: Array<{ name: string | { value?: string } }>,
  bodyParameters: Array<{ name: string | { value?: string } }>,
  footerParameters: Array<{ name: string | { value?: string } }>,
  headerMediaParam?: { type: 'id' | 'link'; value: string | { value?: string } }
) => {
  // Type guard to check if template is a WhatsappTemplate
  const isWhatsappTemplate = (
    t: WhatsappTemplate | EmailTemplate | SmsTemplate | undefined
  ): t is WhatsappTemplate => {
    return t !== undefined && 'id' in t && 'components' in t;
  };

  if (!template || !isWhatsappTemplate(template)) {
    return null;
  }

  // Create a formatted template structure preserving original components
  const formattedTemplate = { ...template, components: [] };

  // Check if we're using named parameters
  const isNamedParams = template.parameter_format === 'NAMED';

  // Helper to check if header is a media header
  const isMediaHeader = (header: { format?: string }) =>
    header?.format && header.format.toLowerCase() !== 'text';

  // Process each original component and add parameters if they exist
  if (template.components && Array.isArray(template.components)) {
    template.components.forEach((originalComponent) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const componentCopy = { ...originalComponent } as any;

      // Add parameters based on component type
      switch (originalComponent.type) {
        case 'HEADER':
          // Handle media headers (IMAGE, VIDEO, DOCUMENT)
          if (isMediaHeader(originalComponent) && headerMediaParam) {
            const mediaType = originalComponent.format.toLowerCase() as
              'image' | 'video' | 'document';
            const value =
              typeof headerMediaParam.value === 'string'
                ? headerMediaParam.value
                : headerMediaParam.value?.value || '';

            componentCopy.parameters = value
              ? [
                  {
                    type: mediaType,
                    [mediaType]: {
                      id: headerMediaParam.type === 'id' ? value : undefined,
                      link: headerMediaParam.type === 'link' ? value : undefined,
                    },
                  },
                ]
              : undefined;
          }
          // Handle text headers with variables
          else if (headerParameters && headerParameters.length > 0) {
            const variableNames = isNamedParams
              ? getVariableNamesFromNamedParams(originalComponent, 'header')
              : [];

            componentCopy.parameters = headerParameters.map((param, index) => {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const paramObject: any = {
                type: 'text',
                text: typeof param.name === 'string' ? param.name : param.name.value || '',
              };

              // Add parameter_name for named parameters
              if (isNamedParams && index < variableNames.length) {
                paramObject.parameter_name = variableNames[index];
              }

              return paramObject;
            });
          }
          break;
        case 'BODY':
          if (bodyParameters && bodyParameters.length > 0) {
            const variableNames = isNamedParams
              ? getVariableNamesFromNamedParams(originalComponent, 'body')
              : [];

            componentCopy.parameters = bodyParameters.map((param, index) => {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const paramObject: any = {
                type: 'text',
                text: typeof param.name === 'string' ? param.name : param.name.value || '',
              };

              // Add parameter_name for named parameters
              if (isNamedParams && index < variableNames.length) {
                paramObject.parameter_name = variableNames[index];
              }

              return paramObject;
            });
          }
          break;
        case 'FOOTER':
          if (footerParameters && footerParameters.length > 0) {
            const variableNames = isNamedParams
              ? getVariableNamesFromNamedParams(originalComponent, 'footer')
              : [];

            componentCopy.parameters = footerParameters.map((param, index) => {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const paramObject: any = {
                type: 'text',
                text: typeof param.name === 'string' ? param.name : param.name.value || '',
              };

              // Add parameter_name for named parameters
              if (isNamedParams && index < variableNames.length) {
                paramObject.parameter_name = variableNames[index];
              }

              return paramObject;
            });
          }
          break;
      }

      formattedTemplate.components.push(componentCopy);
    });
  }

  return formattedTemplate;
};

/**
 * Transform form data to campaign senders for API
 * Creates all senders first, then adds transitions to primary sender pointing to fallbacks
 * @param formData - Campaign form data
 * @returns Array of NewCampaignSender objects
 */
export const transformFormDataToSenders = (formData: CampaignForm): NewCampaignSender[] => {
  const senders: NewCampaignSender[] = [];
  const transitions: CampaignTransition[] = [];

  // Step 1: Create primary sender (without transitions initially)
  if (formData.primarySender.integrationId) {
    const formattedTemplate = formatTemplateComponents(
      formData.primarySender.template,
      formData.primarySender.headerParameters || [],
      formData.primarySender.bodyParameters || [],
      formData.primarySender.footerParameters || [],
      formData.primarySender.headerMediaParam
    );

    senders.push({
      integration_id: formData.primarySender.integrationId,
      template: formattedTemplate,
      // Transitions will be added after fallback senders are created
    });
  }

  // Step 2: Create fallback senders (without transitions)
  if (formData.fallbackIfNotDelivered && formData.fallbackSenders?.[0]?.integrationId) {
    const fallbackSender = formData.fallbackSenders[0];
    const template =
      fallbackSender.integrationType === 'whatsapp'
        ? formatTemplateComponents(
            fallbackSender.template,
            fallbackSender.headerParameters || [],
            fallbackSender.bodyParameters || [],
            fallbackSender.footerParameters || [],
            fallbackSender.headerMediaParam
          )
        : { text: fallbackSender.content };

    senders.push({
      integration_id: fallbackSender.integrationId,
      template,
      // No transitions on fallback senders
    });

    // Add transition to primary sender pointing to this fallback
    transitions.push({ condition: TransitionCondition.DELIVERY_FAILED, after_minutes: 0 });
  }

  // Step 3: Create follow-up senders for no response (index 1+ in fallbackSenders array)
  if (formData.fallbackIfNoResponse && formData.fallbackSenders) {
    // Iterate over all no-response senders starting from index 1
    for (let i = 1; i < formData.fallbackSenders.length; i++) {
      const followupSender = formData.fallbackSenders[i];

      if (followupSender?.integrationId) {
        const template =
          followupSender.integrationType === 'whatsapp'
            ? formatTemplateComponents(
                followupSender.template,
                followupSender.headerParameters || [],
                followupSender.bodyParameters || [],
                followupSender.footerParameters || [],
                followupSender.headerMediaParam
              )
            : { text: followupSender.content };

        senders.push({
          integration_id: followupSender.integrationId,
          template,
          // No transitions on followup senders
        });

        // Add transition to primary sender pointing to this followup
        transitions.push({
          condition: TransitionCondition.NO_RESPONSE,
          after_minutes: followupSender.waitTimeMinutes || DEFAULT_NO_RESPONSE_WAIT_MINUTES,
        });
      }
    }
  }

  // Step 4: Add transitions to primary sender if there are fallbacks
  if (transitions.length > 0 && senders.length > 0) {
    senders[0].transitions = transitions;
  }

  return senders;
};

/**
 * Transform draft senders from Redux to form data.
 * Traverses the chained no-response transitions to build the ordered array.
 *
 * Chain structure: Primary → (no_response) → A → (no_response) → B → ...
 * Wait times come from the transition pointing TO each sender.
 *
 * @param draftSenders - Array of draft campaign senders
 * @returns Partial campaign form data
 */
export const transformDraftSendersToFormData = (
  draftSenders: DraftCampaignSender[]
): Partial<CampaignForm> => {
  // Primary sender is the one WITH transitions (it points to fallbacks)
  const primarySender =
    draftSenders.find((s) => s.transitions && s.transitions.length > 0) || draftSenders[0];

  const formData: Partial<CampaignForm> = {};

  // Transform primary sender
  if (primarySender) {
    formData.primarySender = {
      integrationId: primarySender.integrationId,
      integrationType: primarySender.integrationType as IntegrationType,
      content: primarySender.content,
      subject: primarySender.subject,
      template: primarySender.template,
      headerParameters: primarySender.headerParameters || [],
      bodyParameters: primarySender.bodyParameters || [],
      footerParameters: primarySender.footerParameters || [],
      headerMediaParam: primarySender.headerMediaParam,
      amdOnMachine: primarySender.amdOnMachine,
    };

    // Check transitions to determine which fallbacks are enabled
    if (primarySender.transitions && primarySender.transitions.length > 0) {
      const hasDeliveryFailedTransition = primarySender.transitions.some(
        (t) => t.condition === TransitionCondition.DELIVERY_FAILED
      );
      const noResponseTransition = primarySender.transitions.find(
        (t) => t.condition === TransitionCondition.NO_RESPONSE
      );

      formData.fallbackIfNotDelivered = hasDeliveryFailedTransition;
      formData.fallbackIfNoResponse = !!noResponseTransition;
    }
  }

  // Transform fallback senders
  // Map transitions to their corresponding senders in the correct form structure
  if (primarySender?.transitions && primarySender.transitions.length > 0) {
    const fallbackSendersArray: BaseCampaignSender[] = [];

    // Find sender for delivery_failed transition (goes in index 0)
    const deliveryFailedTransition = primarySender.transitions.find(
      (t) => t.condition === TransitionCondition.DELIVERY_FAILED
    );

    // Track which sender IDs are used for delivery_failed to exclude from no_response
    let deliveryFailedSenderId: string | undefined;

    if (deliveryFailedTransition && deliveryFailedTransition.sender_id) {
      // Find the fallback sender by matching senderId from transition
      const deliveryFailedSender = draftSenders.find(
        (s) => s.senderId === deliveryFailedTransition.sender_id
      );

      if (deliveryFailedSender) {
        deliveryFailedSenderId = deliveryFailedSender.senderId;
        fallbackSendersArray[0] = {
          integrationId: deliveryFailedSender.integrationId,
          integrationType: deliveryFailedSender.integrationType as IntegrationType,
          content: deliveryFailedSender.content,
          subject: deliveryFailedSender.subject,
          template: deliveryFailedSender.template,
          waitTimeMinutes: 0,
          headerParameters: deliveryFailedSender.headerParameters || [],
          bodyParameters: deliveryFailedSender.bodyParameters || [],
          footerParameters: deliveryFailedSender.footerParameters || [],
          headerMediaParam: deliveryFailedSender.headerMediaParam,
          amdOnMachine: deliveryFailedSender.amdOnMachine,
        };
      }
    }

    // Always ensure index 0 has a placeholder if not set (for "not delivered" slot)
    // This ensures "no response" senders always start at index 1
    if (!fallbackSendersArray[0]) {
      fallbackSendersArray[0] = {
        integrationId: '',
        integrationType: '' as IntegrationType,
        content: '',
        subject: '',
        template: undefined,
        headerParameters: [],
        bodyParameters: [],
        footerParameters: [],
        headerMediaParam: undefined,
      };
    }

    // Traverse the no-response chain starting from primary sender
    // Each sender points to the next via its no_response transition
    let currentTransition = primarySender.transitions.find(
      (t) => t.condition === TransitionCondition.NO_RESPONSE
    );
    let chainIndex = 1; // No-response senders start at index 1

    while (currentTransition?.sender_id) {
      // Find the sender in the chain
      const nextSender = draftSenders.find(
        (s) => s.senderId === currentTransition!.sender_id && s.senderId !== deliveryFailedSenderId
      );

      if (!nextSender) {
        break; // Broken chain - stop traversal
      }

      // Add sender to array with wait time from the transition pointing TO it
      fallbackSendersArray[chainIndex] = {
        integrationId: nextSender.integrationId,
        integrationType: nextSender.integrationType as IntegrationType,
        content: nextSender.content,
        subject: nextSender.subject,
        template: nextSender.template,
        waitTimeMinutes: currentTransition.after_minutes || DEFAULT_NO_RESPONSE_WAIT_MINUTES,
        headerParameters: nextSender.headerParameters || [],
        bodyParameters: nextSender.bodyParameters || [],
        footerParameters: nextSender.footerParameters || [],
        headerMediaParam: nextSender.headerMediaParam,
        amdOnMachine: nextSender.amdOnMachine,
      };

      // Follow the chain - look for this sender's no_response transition
      currentTransition = nextSender.transitions?.find(
        (t) => t.condition === TransitionCondition.NO_RESPONSE
      );
      chainIndex++;
    }

    formData.fallbackSenders = fallbackSendersArray;
  }

  return formData;
};

/**
 * Parse a date value (ISO string or Unix timestamp) to Unix timestamp in milliseconds.
 * Accepts: "2024-02-15", "2024-02-15T00:00:00Z", 1707955200000, "1707955200000"
 * @param value - Date string or timestamp
 * @returns Unix timestamp in milliseconds or null if invalid
 */
export function parseDateToTimestamp(value: string): number | null {
  // Already a numeric timestamp (as string)
  if (/^\d{10,13}$/.test(value)) {
    const ts = parseInt(value, 10);
    // Convert seconds to milliseconds if needed
    return ts < 1e12 ? ts * 1000 : ts;
  }

  // ISO date string (YYYY-MM-DD or full ISO)
  if (/^\d{4}-\d{2}-\d{2}/.test(value)) {
    const date = new Date(value);
    if (!isNaN(date.getTime())) {
      return date.getTime();
    }
  }

  return null;
}

/**
 * Coerce a raw `debt.<field>` CSV value into the shape the orchestrator expects.
 *
 * - `amount` → number (parseFloat, NaN falls back to original string so the
 *   server can surface a validation error rather than silently losing data)
 * - `due_date` → Unix timestamp in milliseconds (ISO or epoch)
 * - everything else passes through as-is
 */
function coerceDebtFieldValue(field: string, value: string): unknown {
  if (field === 'amount') {
    const parsed = parseFloat(value);
    return Number.isNaN(parsed) ? value : parsed;
  }
  if (field === 'due_date') {
    const timestamp = parseDateToTimestamp(value);
    return timestamp ?? value;
  }
  return value;
}

/**
 * Helper function to set nested object values from dot notation keys.
 *
 * Treats numeric segments as array indices, so paths like
 *   - `debt.0.amount`                    → context.debts[0].amount
 *   - `debts.0.case_id`                  → context.debts[0].case_id
 *   - `live_instructions.debts.0.x`      → context.live_instructions.debts[0].x
 *   - `alternate_phones.0`               → context.alternate_phones[0]
 * are all resolved by the same walker. The top-level singular `debt` key
 * (emitted by canonical mapped subscribers, e.g. `debt.0.amount`) is renamed
 * to `debts` so it merges into the same array as CSV-native `debts[0].*`
 * paths. Legacy `debt.<field>` (no slot) is folded into slot 0.
 *
 * Orchestrator PR #1839 rejects `context.debt` (singular); all debt specs
 * must live under `context.debts[]`.
 *
 * @param obj - Target object to set the value in
 * @param path - Dot notation path (e.g., "user.display_name" or "debt.0.amount")
 * @param value - Value to set
 */
function setNestedValue(obj: Record<string, unknown>, path: string, value: string): void {
  // Normalize bracket notation `name[N]` → `name.N` so the walker only sees
  // dotted segments.
  const segments = path
    .replace(/\[(\d+)\]/g, '.$1')
    .split('.')
    .filter((s) => s.length > 0);

  // Singular `debt.<N>.<field>` (canonical mapped) → plural `debts.<N>.<field>`
  // so canonical and CSV-native (`debts[N].<field>`) paths merge into the
  // same array. Legacy `debt.<field>` (no slot) → `debts.0.<field>`.
  if (segments[0] === 'debt') {
    if (segments.length >= 2 && /^\d+$/.test(segments[1])) {
      segments[0] = 'debts';
    } else {
      segments[0] = 'debts';
      segments.splice(1, 0, '0');
    }
  }

  // Walk segments. A numeric segment means the previous segment is an
  // array; create the array on demand and index into it.
  let cur: Record<string, unknown> | unknown[] = obj;
  for (let i = 0; i < segments.length - 1; i++) {
    const seg = segments[i];
    const nextIsIndex = /^\d+$/.test(segments[i + 1]);

    if (/^\d+$/.test(seg)) {
      const arr = cur as unknown[];
      const idx = parseInt(seg, 10);
      if (arr[idx] === undefined || arr[idx] === null) {
        arr[idx] = nextIsIndex ? [] : {};
      }
      cur = arr[idx] as Record<string, unknown> | unknown[];
    } else {
      const o = cur as Record<string, unknown>;
      if (o[seg] === undefined || o[seg] === null || typeof o[seg] !== 'object') {
        o[seg] = nextIsIndex ? [] : {};
      }
      cur = o[seg] as Record<string, unknown> | unknown[];
    }
  }

  // Apply debt-field coercion (amount→number, due_date→timestamp) only when
  // we're writing the leaf of a canonical `debts[N].<field>` path, where the
  // field name is one of the orchestrator-known shapes.
  const leaf = segments[segments.length - 1];
  const isCanonicalDebtField =
    segments[0] === 'debts' &&
    segments.length === 3 &&
    /^\d+$/.test(segments[1]) &&
    !/^\d+$/.test(leaf);
  const finalValue = isCanonicalDebtField ? coerceDebtFieldValue(leaf, value) : value;

  if (/^\d+$/.test(leaf)) {
    (cur as unknown[])[parseInt(leaf, 10)] = finalValue;
  } else {
    (cur as Record<string, unknown>)[leaf] = finalValue;
  }
}

/**
 * Transform draft campaign subscribers to API format.
 *
 * Maps phone/email to root level. Everything else goes under `context`:
 * - Plain keys like `residence_state`, `country_code`, `account_id`, custom vars
 *   become top-level `context.<key>`.
 * - Dotted `debt.*` keys collapse into `context.debts[0]` (array entry).
 * - Other dotted keys (e.g., `user.display_name`) build nested objects under context.
 *
 * @param draftSubscribers - Array of draft subscribers from Redux (flattened structure)
 * @returns Array of NewCampaignSubscriber objects for API (structured with context)
 */
export const transformDraftSubscribersToApiFormat = (
  draftSubscribers: DraftCampaignSubscriber[]
): NewCampaignSubscriber[] => {
  return draftSubscribers.map((draftSubscriber) => {
    const apiSubscriber: NewCampaignSubscriber = {};
    const context: Record<string, unknown> = {};

    // Process each property in the draft subscriber
    Object.entries(draftSubscriber).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        // Keep phone and email at root level
        if (key === 'phone') {
          apiSubscriber.phone = value;
        } else if (key === 'email') {
          apiSubscriber.email = value;
        } else {
          // Everything else goes to context
          // Handle dotted keys by creating nested objects (or collapsing debt.* into debts[])
          setNestedValue(context, key, value);
        }
      }
    });

    // Compact sparse alternate_* arrays so gaps from unmapped slots (e.g.
    // user mapped slots .0 and .2 but not .1) don't become `undefined` entries
    // in the JSON payload. Empty arrays drop out entirely.
    for (const bucket of ['alternate_phones', 'alternate_emails'] as const) {
      const arr = context[bucket];
      if (Array.isArray(arr)) {
        const compact = (arr as unknown[]).filter(
          (v): v is string => typeof v === 'string' && v.length > 0
        );
        if (compact.length > 0) {
          context[bucket] = compact;
        } else {
          delete context[bucket];
        }
      }
    }

    // Same for `context.debts[]` — drop empty slot objects so a sparse
    // `debt.1.<field>` mapping (without slot 0) yields `[{...}]`, not
    // `[{}, {...}]` which the orchestrator's `.or(...)` constraint rejects.
    if (Array.isArray(context.debts)) {
      const compact = (context.debts as Record<string, unknown>[]).filter(
        (d) => d && typeof d === 'object' && Object.keys(d).length > 0
      );
      if (compact.length > 0) {
        context.debts = compact;
      } else {
        delete context.debts;
      }
    }

    // Only add context if it has properties
    if (Object.keys(context).length > 0) {
      apiSubscriber.context = context;
    }

    return apiSubscriber;
  });
};

/**
 * Whether the given integration has Answering Machine Detection turned on.
 * Only voice integrations carry `config.amd`; everything else returns false.
 *
 * @param integrations - Integrations available to the campaign
 * @param integrationId - Integration selected on the sender
 * @returns true when the integration reports AMD enabled
 */
export const isAmdEnabledIntegration = (
  integrations: Array<{ integration_id?: string; config?: unknown }>,
  integrationId?: string
): boolean => {
  const integration = integrations.find((i) => i.integration_id === integrationId);
  const config = integration?.config as TelnyxVoiceConfig | TwilioVoiceConfig | undefined;
  return Boolean(config?.amd?.enabled);
};

/** Voice integrations that support the campaign opener settings. */
export const isVoiceIntegrationType = (integrationType?: string): boolean =>
  integrationType === IntegrationType.TWILIO_VOICE ||
  integrationType === IntegrationType.TELNYX_VOICE ||
  integrationType === IntegrationType.AGORA_VOICE;

/**
 * Which sender renders the campaign-level opener block: the first voice sender
 * that the save will actually reach.
 *
 * The settings are campaign-wide, so they render once. Anchoring them to the
 * first voice sender rather than to the primary keeps them visible when only a
 * fallback is voice — otherwise they would still be written to that fallback,
 * with no control to see or reset them.
 *
 * The enabled flags mirror `saveCampaignSendersSequentially`: a fallback that
 * will not be created cannot own the block.
 */
export const voiceSettingsSenderPath = (formData: Partial<CampaignForm>): string | undefined => {
  if (isVoiceIntegrationType(formData.primarySender?.integrationType)) {
    return 'primarySender';
  }

  const index = (formData.fallbackSenders ?? []).findIndex((sender, i) => {
    const willBeSaved = i === 0 ? formData.fallbackIfNotDelivered : formData.fallbackIfNoResponse;
    return Boolean(willBeSaved) && isVoiceIntegrationType(sender?.integrationType);
  });

  return index === -1 ? undefined : `fallbackSenders.${index}`;
};

/**
 * A delay is only worth storing when it is a sane integer that differs from the
 * platform default. Everything else (NaN, out of range, a string a raw API
 * client stored, the default itself) resolves to the platform default anyway.
 */
const isStorableDelay = (delay: unknown): delay is number =>
  typeof delay === 'number' &&
  Number.isInteger(delay) &&
  delay >= 0 &&
  delay <= MAX_FIRST_MESSAGE_DELAY_MS &&
  delay !== DEFAULT_FIRST_MESSAGE_DELAY_MS;

/**
 * Translate the campaign-level opener settings into their wire shape. Resolve
 * this once per save and hand the result to every sender.
 *
 * Only values that differ from the platform default travel: an absent key is
 * what tells the voice pipeline to apply its own default, so a campaign that
 * kept the defaults keeps inheriting them if they ever change. This is the
 * last stop before the wire and Save & Exit skips form validation, so the
 * sanity checks live here.
 */
export const voiceOpenerMetadata = (voiceSettings?: VoiceCallSettings): CampaignSenderMetadata => ({
  // The default is non-interruptible, so only opting in is worth storing
  ...(voiceSettings?.firstMessageInterruptible === true && { first_message_interruptible: true }),
  ...(isStorableDelay(voiceSettings?.firstMessageDelayMs) && {
    first_message_delay_ms: voiceSettings?.firstMessageDelayMs,
  }),
});

/**
 * Read the campaign-level opener settings back off the senders.
 *
 * Every authored voice sender carries the same values (see
 * `saveCampaignSendersSequentially`), so any one of them is authoritative.
 * NBA follow-up senders are backend-generated and skipped: they may predate
 * the settings and would shadow the authored values.
 */
export const readVoiceCallSettings = (senders: CampaignSender[]): VoiceCallSettings => {
  const metadata = senders.find(
    (sender) => isVoiceIntegrationType(sender.integration_type) && !sender.metadata?.nba_followup
  )?.metadata;

  return {
    firstMessageInterruptible: metadata?.first_message_interruptible,
    firstMessageDelayMs: metadata?.first_message_delay_ms,
  };
};

/**
 * Build the sender metadata, or nothing when every value kept the platform
 * default. Spread into a `NewCampaignSender` payload.
 *
 * @param sender - Sender being created, for its own per-sender keys
 * @param openerMetadata - Campaign-level opener keys, already in wire shape
 * @returns `{ metadata }` or an empty object
 */
export const buildSenderMetadata = (
  sender: Pick<BaseCampaignSender, 'amdOnMachine' | 'integrationType'>,
  openerMetadata?: CampaignSenderMetadata
): Pick<NewCampaignSender, 'metadata'> => {
  const metadata: CampaignSenderMetadata = {
    // Opener settings only mean anything on a voice call
    ...(isVoiceIntegrationType(sender.integrationType) && openerMetadata),
  };

  if (sender.amdOnMachine) {
    metadata.amd_on_machine = sender.amdOnMachine;
  }

  return Object.keys(metadata).length > 0 ? { metadata } : {};
};

/**
 * Save campaign senders sequentially with chained no-response transitions.
 *
 * The backend only supports ONE transition per condition type per sender.
 * For multiple no-response fallbacks, we chain them:
 *   Primary → (no_response) → Sender A → (no_response) → Sender B → ...
 *
 * @param campaignId - Campaign ID
 * @param formData - Campaign form data
 * @param createSenderFn - Function to create a campaign sender
 * @param updateSenderFn - Function to update a campaign sender
 */
export const saveCampaignSendersSequentially = async (
  campaignId: string,
  formData: CampaignForm | Partial<CampaignForm>,
  createSenderFn: (data: {
    campaignId: string;
    sender: NewCampaignSender;
  }) => Promise<CampaignSender>,
  updateSenderFn: (data: PartialCampaignSender & { campaign_id: string }) => Promise<CampaignSender>
): Promise<void> => {
  // Step 1: Create primary sender (without transitions)
  if (!formData.primarySender?.integrationId) {
    throw new Error('Primary sender integration is required');
  }

  // Campaign-level, so every voice sender gets the same values and the first
  // call cannot diverge from the follow-ups
  const openerMetadata = voiceOpenerMetadata(formData.voiceSettings);

  // Build template for primary sender, skip template if empty
  let primarySenderTemplate;
  if (formData.primarySender?.integrationType === 'whatsapp') {
    primarySenderTemplate = formatTemplateComponents(
      formData.primarySender.template,
      formData.primarySender.headerParameters || [],
      formData.primarySender.bodyParameters || [],
      formData.primarySender.footerParameters || [],
      formData.primarySender.headerMediaParam
    );
  } else if (formData.primarySender?.integrationType === IntegrationType.EMAIL) {
    const subject = formData.primarySender.subject?.trim() || '';
    const body = formData.primarySender.content?.trim() || '';
    // Only create template if both subject and body have content
    if (subject && body) {
      primarySenderTemplate = { subject, body };
    }
  } else {
    const text = formData.primarySender.content?.trim() || '';
    if (text) {
      primarySenderTemplate = { text };
    }
  }

  const primarySender = await createSenderFn({
    campaignId,
    sender: {
      integration_id: formData.primarySender.integrationId,
      ...(primarySenderTemplate && { template: primarySenderTemplate }),
      ...buildSenderMetadata(formData.primarySender, openerMetadata),
      // No transitions initially
    },
  });

  // Step 2: Create fallback senders and collect their IDs
  const primaryTransitions: CampaignTransition[] = [];
  const noResponseSenderIds: string[] = [];
  const noResponseWaitTimes: number[] = [];

  // Create delivery failed fallback sender
  if (formData.fallbackIfNotDelivered && formData.fallbackSenders?.[0]?.integrationId) {
    const fallbackSender = formData.fallbackSenders[0];

    // Build template, skip template if empty
    let fallbackTemplate;
    if (fallbackSender.integrationType === 'whatsapp') {
      fallbackTemplate = formatTemplateComponents(
        fallbackSender.template,
        fallbackSender.headerParameters || [],
        fallbackSender.bodyParameters || [],
        fallbackSender.footerParameters || [],
        fallbackSender.headerMediaParam
      );
    } else if (fallbackSender.integrationType === IntegrationType.EMAIL) {
      const subject = fallbackSender.subject?.trim() || '';
      const body = fallbackSender.content?.trim() || '';
      // Only create template if both subject and body have content
      if (subject && body) {
        fallbackTemplate = { subject, body };
      }
    } else {
      const text = fallbackSender.content?.trim() || '';
      if (text) {
        fallbackTemplate = { text };
      }
    }

    // Always create fallback sender with integration_id, include template only if it has content
    const createdFallbackSender = await createSenderFn({
      campaignId,
      sender: {
        integration_id: fallbackSender.integrationId,
        ...(fallbackTemplate && { template: fallbackTemplate }),
        ...buildSenderMetadata(fallbackSender, openerMetadata),
      },
    });

    // Delivery failed transition goes on primary sender
    primaryTransitions.push({
      condition: TransitionCondition.DELIVERY_FAILED,
      after_minutes: 0,
      sender_id: createdFallbackSender.sender_id,
    });
  }

  // Create no response followup senders (index 1+ in fallbackSenders array)
  if (formData.fallbackIfNoResponse && formData.fallbackSenders) {
    // Iterate over all no-response senders starting from index 1
    for (let i = 1; i < formData.fallbackSenders.length; i++) {
      const followupSender = formData.fallbackSenders[i];

      if (followupSender?.integrationId) {
        // Build template, skip template if empty
        let followupTemplate;
        if (followupSender.integrationType === 'whatsapp') {
          followupTemplate = formatTemplateComponents(
            followupSender.template,
            followupSender.headerParameters || [],
            followupSender.bodyParameters || [],
            followupSender.footerParameters || [],
            followupSender.headerMediaParam
          );
        } else if (followupSender.integrationType === IntegrationType.EMAIL) {
          const subject = followupSender.subject?.trim() || '';
          const body = followupSender.content?.trim() || '';
          // Only create template if both subject and body have content
          if (subject && body) {
            followupTemplate = { subject, body };
          }
        } else {
          const text = followupSender.content?.trim() || '';
          if (text) {
            followupTemplate = { text };
          }
        }

        // Create followup sender with integration_id, include template only if it has content
        const createdFollowupSender = await createSenderFn({
          campaignId,
          sender: {
            integration_id: followupSender.integrationId,
            ...(followupTemplate && { template: followupTemplate }),
            ...buildSenderMetadata(followupSender, openerMetadata),
          },
        });

        // Collect sender IDs and wait times for chaining
        noResponseSenderIds.push(createdFollowupSender.sender_id);
        noResponseWaitTimes.push(
          followupSender.waitTimeMinutes || DEFAULT_NO_RESPONSE_WAIT_MINUTES
        );
      }
    }
  }

  // Step 3: Build chained transitions
  // Primary sender only gets transition to the FIRST no-response sender
  if (noResponseSenderIds.length > 0) {
    primaryTransitions.push({
      condition: TransitionCondition.NO_RESPONSE,
      after_minutes: noResponseWaitTimes[0],
      sender_id: noResponseSenderIds[0],
    });
  }

  // Update primary sender with its transitions
  if (primaryTransitions.length > 0) {
    await updateSenderFn({
      sender_id: primarySender.sender_id,
      campaign_id: campaignId,
      transitions: primaryTransitions,
    });
  }

  // Step 4: Chain subsequent no-response senders
  // Each no-response sender (except the last) points to the next one
  for (let i = 0; i < noResponseSenderIds.length - 1; i++) {
    await updateSenderFn({
      sender_id: noResponseSenderIds[i],
      campaign_id: campaignId,
      transitions: [
        {
          condition: TransitionCondition.NO_RESPONSE,
          after_minutes: noResponseWaitTimes[i + 1],
          sender_id: noResponseSenderIds[i + 1],
        },
      ],
    });
  }
  // Note: The last no-response sender has no transitions (terminal sender)
};

/**
 * Replace all campaign senders: delete existing ones and create new ones
 * This is simpler and cleaner than trying to update existing senders
 * @param campaignId - Campaign ID
 * @param formData - Campaign form data
 * @param existingSenders - Array of existing campaign senders to delete
 * @param createSenderFn - Function to create a campaign sender
 * @param updateSenderFn - Function to update a campaign sender (for final transitions)
 * @param deleteSenderFn - Function to delete a campaign sender
 */
export const replaceCampaignSendersSequentially = async (
  campaignId: string,
  formData: CampaignForm | Partial<CampaignForm>,
  existingSenders: CampaignSender[],
  createSenderFn: (data: {
    campaignId: string;
    sender: NewCampaignSender;
  }) => Promise<CampaignSender>,
  updateSenderFn: (
    data: PartialCampaignSender & { campaign_id: string }
  ) => Promise<CampaignSender>,
  deleteSenderFn: (campaignId: string, senderId: string) => Promise<CampaignSender>
): Promise<void> => {
  if (!formData.primarySender?.integrationId) {
    throw new Error('Primary sender integration is required');
  }

  // Step 1: Delete all existing senders first
  for (const existingSender of existingSenders) {
    await deleteSenderFn(campaignId, existingSender.sender_id);
  }

  // Step 2: Create fresh senders using the original create logic
  await saveCampaignSendersSequentially(campaignId, formData, createSenderFn, updateSenderFn);
};

// ============================================================================
// VALIDATION ERROR UTILITIES
// ============================================================================

/**
 * Interface for validation error objects returned by the API
 */
export interface ValidationError {
  index?: number;
  field?: string;
  message?: string;
  error?: string;
  value?: string;
}

/**
 * Generate user-friendly error message based on validation error from API
 * @param error - Validation error object from API response
 * @param t - Translation function
 * @returns User-friendly error message
 */
export const getValidationErrorMessage = (
  error: ValidationError,
  t: (key: string) => string
): string => {
  // Handle new error format with field and message
  if (error.field && error.message) {
    if (error.field === 'phone') {
      return t('campaigns.invalid_phone_number');
    }
    if (error.field === 'email') {
      return t('campaigns.invalid_email_address');
    }

    // Handle duplicate subscriber errors
    if (error.field === 'subscriber' && error.message.includes('duplicate')) {
      // Check if the duplicate value looks like a phone or email
      const duplicateValue = error.value || '';
      if (duplicateValue.includes('@')) {
        return t('campaigns.email_already_used');
      } else if (duplicateValue.match(/^[+]?[0-9\s\-()]+$/)) {
        return t('campaigns.phone_already_used');
      } else {
        // Generic duplicate message if we can't determine the type
        return t('campaigns.subscriber_already_exists');
      }
    }

    // For other field-specific errors, return the message as is
    return error.message;
  }

  const errorMessage = error.error || error.message || '';
  // Fallback to the original error message or generic error
  return errorMessage || t('campaigns.subscriber_validation_error');
};

/**
 * Determine which form field should show the error based on validation error
 * @param error - Validation error object
 * @param trimmedPhone - User's entered phone value
 * @param trimmedEmail - User's entered email value
 * @returns Field name ('phone', 'email') or null if should show toast
 */
export const getErrorFieldName = (error: ValidationError): 'phone' | 'email' | null => {
  // Direct field mapping
  if (error.field === 'phone') {
    return 'phone';
  }
  if (error.field === 'email') {
    return 'email';
  }

  // For duplicate errors, determine field based on duplicate value
  if (error.field === 'subscriber' && error.value) {
    if (error.value.includes('@')) {
      return 'email';
    }
    if (error.value.match(/^[+]?[0-9\s\-()]+$/)) {
      return 'phone';
    }
  }

  // Can't determine field - should show toast
  return null;
};

/**
 * Process validation errors from API response and set form errors appropriately
 * @param verificationResult - API response (array of errors or object with errors)
 * @param existingSubscribers - Array of existing subscribers
 * @param form - React Hook Form instance
 * @param t - Translation function
 * @param trimmedPhone - User's entered phone value
 * @param trimmedEmail - User's entered email value
 * @returns true if there were validation errors, false if validation passed
 */
export const handleValidationErrors = (
  verificationResult: ValidationError[] | { errors?: ValidationError[] } | unknown,
  existingSubscribers: unknown[],
  form: { setError: (field: string, error: { type: string; message: string }) => void },
  t: (key: string) => string
): boolean => {
  // Handle different response formats
  let errors: ValidationError[] = [];

  if (Array.isArray(verificationResult)) {
    errors = verificationResult;
  } else if (
    verificationResult &&
    typeof verificationResult === 'object' &&
    'errors' in verificationResult
  ) {
    errors = (verificationResult as { errors?: ValidationError[] }).errors || [];
  }

  // No errors found
  if (errors.length === 0) {
    return false;
  }

  // Check if the new subscriber (at index = existing subscribers count) has errors
  const newSubscriberIndex = existingSubscribers.length;
  const newSubscriberError = errors.find((error) => error.index === newSubscriberIndex);

  if (newSubscriberError) {
    const errorMessage = getValidationErrorMessage(newSubscriberError, t);
    const fieldName = getErrorFieldName(newSubscriberError);

    if (fieldName) {
      form.setError(fieldName, { type: 'manual', message: errorMessage });
    } else {
      // Fallback to toast if we can't determine which field
      toast.error(errorMessage);
    }

    return true; // Has validation errors
  }

  return false; // No errors for this subscriber
};

// ============================================================================
// TIME AND TIMEZONE UTILITIES
// ============================================================================

/**
 * Timezone option interface for timezone selectors
 */
export interface TimezoneOption {
  value: string;
  label: string;
}

/**
 * Common timezones list for campaign scheduling
 */
export const COMMON_TIMEZONES: TimezoneOption[] = [
  { value: 'UTC-12:00', label: 'UTC-12:00 Baker Island' },
  { value: 'UTC-11:00', label: 'UTC-11:00 American Samoa' },
  { value: 'UTC-10:00', label: 'UTC-10:00 Hawaii' },
  { value: 'UTC-09:00', label: 'UTC-09:00 Alaska' },
  { value: 'UTC-08:00', label: 'UTC-08:00 Pacific Time' },
  { value: 'UTC-07:00', label: 'UTC-07:00 Mountain Time' },
  { value: 'UTC-06:00', label: 'UTC-06:00 Central Time' },
  { value: 'UTC-05:00', label: 'UTC-05:00 Eastern Time' },
  { value: 'UTC-04:00', label: 'UTC-04:00 Atlantic Time' },
  { value: 'UTC-03:00', label: 'UTC-03:00 Brazil' },
  { value: 'UTC-02:00', label: 'UTC-02:00 Mid-Atlantic' },
  { value: 'UTC-01:00', label: 'UTC-01:00 Azores' },
  { value: 'UTC+00:00', label: 'UTC+00:00 London' },
  { value: 'UTC+01:00', label: 'UTC+01:00 Paris' },
  { value: 'UTC+02:00', label: 'UTC+02:00 Cairo' },
  { value: 'UTC+03:00', label: 'UTC+03:00 Athens' },
  { value: 'UTC+04:00', label: 'UTC+04:00 Dubai' },
  { value: 'UTC+05:00', label: 'UTC+05:00 Karachi' },
  { value: 'UTC+06:00', label: 'UTC+06:00 Dhaka' },
  { value: 'UTC+07:00', label: 'UTC+07:00 Bangkok' },
  { value: 'UTC+08:00', label: 'UTC+08:00 Singapore' },
  { value: 'UTC+09:00', label: 'UTC+09:00 Tokyo' },
  { value: 'UTC+10:00', label: 'UTC+10:00 Sydney' },
  { value: 'UTC+11:00', label: 'UTC+11:00 Solomon Islands' },
  { value: 'UTC+12:00', label: 'UTC+12:00 New Zealand' },
];

/**
 * Get the user's current timezone in UTC format (e.g., "UTC+02:00")
 * @returns User's timezone as a UTC offset string
 */
export function getUserTimezone(): string {
  const userTimezoneOffset = -new Date().getTimezoneOffset();
  const offsetHours = Math.floor(Math.abs(userTimezoneOffset) / 60);
  const offsetMinutes = Math.abs(userTimezoneOffset) % 60;
  const sign = userTimezoneOffset >= 0 ? '+' : '-';
  return `UTC${sign}${offsetHours.toString().padStart(2, '0')}:${offsetMinutes.toString().padStart(2, '0')}`;
}

/**
 * Parse timezone offset string and return offset in minutes
 * @param timezoneString - Timezone string in format "UTC±HH:MM"
 * @returns Offset in minutes (positive for UTC+, negative for UTC-)
 */
export function parseTimezoneOffset(timezoneString: string): number | null {
  const timezoneOffset = timezoneString.replace('UTC', '');
  const match = timezoneOffset.match(/([+-])(\d+):(\d+)/);

  if (!match) {
    return null; // Invalid timezone format
  }

  const [, sign, tzHours, tzMinutes] = match;
  return (parseInt(tzHours) * 60 + parseInt(tzMinutes)) * (sign === '+' ? 1 : -1);
}

/**
 * Format a date for display in campaign scheduling
 * @param date - Date to format
 * @returns Formatted date string or empty string if date is undefined
 */
export function formatCampaignDate(date: Date | undefined): string {
  if (!date) {
    return '';
  }
  return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

/**
 * Calculate scheduled start time for campaign from form data
 * @param isScheduled - Whether scheduling is enabled
 * @param selectedDate - Selected date
 * @param selectedTime - Selected time in HH:MM format
 * @param selectedTimezone - Selected timezone in UTC±HH:MM format
 * @returns Unix timestamp in milliseconds or 'now' if not scheduled
 */
export function calculateScheduledStartTime(
  isScheduled: boolean,
  selectedDate: Date | undefined,
  selectedTime: string,
  selectedTimezone: string
): number | 'now' {
  if (!isScheduled || !selectedDate || !selectedTime || !selectedTimezone) {
    return 'now';
  }

  const [timeHours, timeMinutes] = selectedTime.split(':');

  // Convert timezone offset to minutes
  const offsetMinutes = parseTimezoneOffset(selectedTimezone);
  if (offsetMinutes === null) {
    return 'now'; // Invalid timezone format
  }

  // Get the date components from the selected date
  const year = selectedDate.getFullYear();
  const month = selectedDate.getMonth();
  const day = selectedDate.getDate();

  // Create the datetime using Date.UTC (which treats values as UTC)
  // This gives us "October 10, 1:20 PM UTC" for example
  const utcTimestamp = Date.UTC(
    year,
    month,
    day,
    parseInt(timeHours, 10),
    parseInt(timeMinutes, 10),
    0,
    0
  );

  // Now adjust for the selected timezone
  // If the selected timezone is UTC+3, "1:20 PM UTC+3" is actually "10:20 AM UTC"
  // So we need to subtract the offset: UTC time = selected timezone time - offset
  const adjustedTimestamp = utcTimestamp - offsetMinutes * 60 * 1000;

  // Return Unix timestamp in milliseconds
  return adjustedTimestamp;
}

/**
 * Restore schedule data from campaign schedule and metadata
 * @param scheduleStartAt - Campaign schedule start_at value
 * @param savedTimezone - Timezone from campaign metadata
 * @returns Restored schedule data or null if not restorable
 */
export function restoreScheduleFromCampaign(
  scheduleStartAt: string | undefined,
  savedTimezone: string | undefined
): { selectedDate: Date; selectedTime: string; selectedTimezone: string } | null {
  if (!scheduleStartAt || scheduleStartAt === 'now' || !savedTimezone) {
    return null;
  }

  try {
    // Handle both ISO string and timestamp formats
    let utcTimestamp: number;

    // Check if it's an ISO string (contains 'T' or '-')
    if (scheduleStartAt.includes('T') || scheduleStartAt.includes('-')) {
      utcTimestamp = new Date(scheduleStartAt).getTime();
    } else {
      utcTimestamp = parseInt(scheduleStartAt, 10);
    }

    const now = new Date();

    // Only restore if the scheduled time is in the future
    if (utcTimestamp <= now.getTime()) {
      return null;
    }

    // Convert UTC timestamp to Date
    const utcDateTime = new Date(utcTimestamp);

    // Parse the saved timezone offset
    const offsetMinutes = parseTimezoneOffset(savedTimezone);
    if (offsetMinutes === null) {
      return null;
    }

    // Convert UTC to local time in the saved timezone
    const localDateTime = new Date(utcDateTime.getTime() + offsetMinutes * 60 * 1000);

    // Use UTC methods to extract the components since we've already adjusted the timestamp
    const selectedDate = new Date(
      localDateTime.getUTCFullYear(),
      localDateTime.getUTCMonth(),
      localDateTime.getUTCDate()
    );
    const selectedTime = `${localDateTime
      .getUTCHours()
      .toString()
      .padStart(2, '0')}:${localDateTime.getUTCMinutes().toString().padStart(2, '0')}`;

    return { selectedDate, selectedTime, selectedTimezone: savedTimezone };
  } catch (error) {
    console.error('Error restoring schedule from campaign:', error);
    return null;
  }
}

/**
 * Validate if a scheduled time is in the future
 * @param selectedDate - Selected date
 * @param selectedTime - Selected time in HH:MM format
 * @param selectedTimezone - Selected timezone in UTC±HH:MM format
 * @returns true if the scheduled time is in the future, false otherwise
 */
export function isScheduledTimeInFuture(
  selectedDate: Date,
  selectedTime: string,
  selectedTimezone: string
): boolean {
  // Use the same calculation logic as calculateScheduledStartTime to ensure consistency
  const scheduledStartTime = calculateScheduledStartTime(
    true,
    selectedDate,
    selectedTime,
    selectedTimezone
  );

  if (scheduledStartTime === 'now') {
    return false;
  }

  const now = Date.now();
  return scheduledStartTime > now;
}

/**
 * Extract phone number from integration name and normalize it
 * @param integrationName - Integration name that may contain a phone number (e.g., "WhatsApp +1 940-642-3857")
 * @returns Normalized phone number (e.g., "+19406423857") or null if not found
 */
export function getPhoneFromIntegrationName(integrationName?: string): string | null {
  if (!integrationName) {
    return null;
  }
  // Match phone number starting with + followed by digits, spaces, dashes, or parentheses
  const phoneMatch = integrationName.match(/\+[\d\s\-()]+/);
  if (!phoneMatch) {
    return null;
  }
  // Normalize by removing all non-digit characters except the leading +
  const normalizedPhone = `+${phoneMatch[0].slice(1).replace(/\D/g, '')}`;
  return normalizedPhone;
}

/**
 * Get a human-readable message showing when a campaign will be sent
 * @param isScheduled - Whether the campaign is scheduled
 * @param selectedDate - Selected date
 * @param selectedTime - Selected time in HH:MM format
 * @param selectedTimezone - Selected timezone in UTC±HH:MM format
 * @returns Formatted string like "2 days and 4 hours from now" or null if not scheduled
 */
export function getScheduledTimeMessage(
  isScheduled: boolean,
  selectedDate: Date | undefined,
  selectedTime: string,
  selectedTimezone: string
): string | null {
  if (!isScheduled || !selectedDate || !selectedTime || !selectedTimezone) {
    return null;
  }

  const scheduledStartTime = calculateScheduledStartTime(
    isScheduled,
    selectedDate,
    selectedTime,
    selectedTimezone
  );

  if (scheduledStartTime === 'now') {
    return null;
  }

  const now = Date.now();
  const diff = scheduledStartTime - now;

  if (diff <= 0) {
    return null;
  }

  // Calculate total hours and minutes for better formatting
  const totalHours = diff / (1000 * 60 * 60);
  const totalMinutes = diff / (1000 * 60);

  // For times less than 1 hour, show minutes
  if (totalHours < 1) {
    const minutes = Math.floor(totalMinutes);
    return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`;
  }

  // For times less than 23 hours, show hours and minutes
  if (totalHours < 23) {
    const hours = Math.floor(totalHours);
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (minutes === 0) {
      return `${hours} ${hours === 1 ? 'hour' : 'hours'}`;
    }

    return `${hours} ${hours === 1 ? 'hour' : 'hours'} and ${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`;
  }

  // For 23+ hours, show in days for better user experience
  // Round to nearest day if close (within 1 hour of a full day)
  const days = Math.floor(totalHours / 24);
  const remainingHours = Math.floor(totalHours % 24);

  // If it's 23+ hours but less than 25 hours, show as "1 day"
  if (days === 0 && remainingHours >= 23) {
    return '1 day';
  }

  // If remaining hours is 23 or more, round up to the next day
  if (remainingHours >= 23) {
    return `${days + 1} ${days + 1 === 1 ? 'day' : 'days'}`;
  }

  // Build the message for days and hours
  const parts: string[] = [];
  parts.push(`${days} ${days === 1 ? 'day' : 'days'}`);

  if (remainingHours > 0) {
    parts.push(`${remainingHours} ${remainingHours === 1 ? 'hour' : 'hours'}`);
  }

  return `${parts.join(' and ')}`;
}

/**
 * Check if the first message configuration is complete based on integration type
 * @param sender - The draft sender configuration
 * @param selectedChannelId - The currently selected channel integration ID
 * @returns true if the message is complete, false otherwise
 *
 * For WhatsApp: requires template selection AND all parameters filled
 * For Email/SMS/RCS/Voice: requires content to be filled
 */
export function isFirstMessageComplete(
  sender: DraftCampaignSender | null,
  selectedChannelId: string
): boolean {
  // Check if sender exists
  if (!sender) {
    return false;
  }

  // Skip if integration ID doesn't match selected channel
  if (sender.integrationId !== selectedChannelId) {
    return false;
  }

  const integrationType = sender.integrationType;

  // For WhatsApp, check if template is selected AND all parameters are filled
  if (integrationType === IntegrationType.WHATSAPP) {
    if (!sender.template?.id || sender.template.id.trim() === '') {
      return false;
    }

    // Check header parameters are filled
    const headerParams = sender.headerParameters;
    if (headerParams && headerParams.length > 0) {
      const hasEmptyHeaderParam = headerParams.some(
        (param) => !param.name || param.name.toString().trim() === ''
      );
      if (hasEmptyHeaderParam) {
        return false;
      }
    }

    // Check body parameters are filled
    const bodyParams = sender.bodyParameters;
    if (bodyParams && bodyParams.length > 0) {
      const hasEmptyBodyParam = bodyParams.some(
        (param) => !param.name || param.name.toString().trim() === ''
      );
      if (hasEmptyBodyParam) {
        return false;
      }
    }

    // Check footer parameters are filled
    const footerParams = sender.footerParameters;
    if (footerParams && footerParams.length > 0) {
      const hasEmptyFooterParam = footerParams.some(
        (param) => !param.name || param.name.toString().trim() === ''
      );
      if (hasEmptyFooterParam) {
        return false;
      }
    }

    return true;
  }

  // For Email, SMS, RCS, Viber (MSTAT), Voice - check if content is filled
  if (
    integrationType === IntegrationType.EMAIL ||
    integrationType === IntegrationType.INFOBIP_SMS ||
    integrationType === IntegrationType.INFOBIP_RCS ||
    integrationType === IntegrationType.MSTAT_VIBER ||
    integrationType === IntegrationType.TWILIO_VOICE ||
    integrationType === IntegrationType.TELNYX_VOICE ||
    integrationType === IntegrationType.AGORA_VOICE
  ) {
    if (sender.content && sender.content.trim() !== '') {
      return true;
    }
  }

  return false;
}

// ============================================================================
// SEQUENTIAL SENDER UTILITIES
// ============================================================================

/**
 * Minimal integration shape needed by sequential sender utilities
 */
interface IntegrationLike {
  integration_id?: string;
  type?: string;
}

/**
 * Reconstruct the full selected-channel list for an autonomous (NBA) campaign on reopen.
 *
 * Autonomous campaigns persist additional channels in `nba_config.integration_mappings`,
 * NOT as `campaign_senders` rows (only the primary voice sender is written at save time;
 * follow-up channels are created lazily at runtime). So on reopen the channel list must be
 * derived from `integration_mappings` — otherwise a saved WhatsApp/second channel silently
 * disappears (MOV-17461). The primary sender's integration stays first.
 */
export const reconstructAutonomousChannels = (
  primaryIntegrationId: string,
  integrationMappings: NBAIntegrationMappings | undefined
): string[] => {
  const additional = integrationMappings
    ? Object.values(integrationMappings).filter(
        (id): id is string => !!id && id !== primaryIntegrationId
      )
    : [];
  return [primaryIntegrationId, ...additional];
};

/**
 * Build the full sequential senders array from selected channels,
 * preserving any existing sender data (content, templates, etc.).
 *
 * Rules:
 * - Index 0 is the primary sender
 * - If primary is WhatsApp, a WA follow-up sender (same integration) is appended
 * - Additional channels (index 1+) are added as follow-ups
 * - Duplicate WhatsApp follow-ups are skipped when primary is already WA
 * - Existing sender data is matched by integrationId to preserve user-entered content
 *
 * @param channels - Array of selected integration IDs (index 0 = primary)
 * @param integrations - Available integrations to resolve types from
 * @param existingSenders - Current senders in Redux to preserve data from
 * @returns New senders array
 */
export const buildSequentialSenders = (
  channels: string[],
  integrations: IntegrationLike[],
  existingSenders: DraftCampaignSender[] | undefined
): DraftCampaignSender[] => {
  const senders: DraftCampaignSender[] = [];

  // Primary sender (index 0)
  const primaryIntegration = integrations.find((i) => i.integration_id === channels[0]);
  const existingPrimary = existingSenders?.find((s) => s.integrationId === channels[0]);
  senders.push({
    integrationId: channels[0] || '',
    integrationType: primaryIntegration?.type || '',
    content: existingPrimary?.content || '',
    subject: existingPrimary?.subject || '',
    template: existingPrimary?.template,
    headerParameters: existingPrimary?.headerParameters || [],
    bodyParameters: existingPrimary?.bodyParameters || [],
    footerParameters: existingPrimary?.footerParameters || [],
    headerMediaParam: existingPrimary?.headerMediaParam,
  });

  const primaryIsWhatsApp = primaryIntegration?.type === IntegrationType.WHATSAPP;

  // If primary is WhatsApp, add a WA follow-up template (same integration)
  if (primaryIsWhatsApp) {
    const existingWaFollowUp = existingSenders?.find(
      (s, idx) => idx > 0 && s.integrationType === IntegrationType.WHATSAPP
    );
    senders.push({
      integrationId: channels[0] || '',
      integrationType: IntegrationType.WHATSAPP,
      content: existingWaFollowUp?.content || '',
      subject: existingWaFollowUp?.subject || '',
      template: existingWaFollowUp?.template,
      headerParameters: existingWaFollowUp?.headerParameters || [],
      bodyParameters: existingWaFollowUp?.bodyParameters || [],
      footerParameters: existingWaFollowUp?.footerParameters || [],
      headerMediaParam: existingWaFollowUp?.headerMediaParam,
    });
  }

  // Additional channels as follow-ups
  for (let i = 1; i < channels.length; i++) {
    const channelId = channels[i];
    if (!channelId) {
      continue;
    }
    const integration = integrations.find((intg) => intg.integration_id === channelId);
    if (!integration) {
      continue;
    }
    // Skip WhatsApp if primary is already WA (follow-up was added above)
    if (primaryIsWhatsApp && integration.type === IntegrationType.WHATSAPP) {
      continue;
    }

    const existingSender = existingSenders?.find(
      (s, idx) => idx > 0 && s.integrationId === channelId
    );
    senders.push({
      integrationId: channelId,
      integrationType: integration.type || '',
      content: existingSender?.content || '',
      subject: existingSender?.subject || '',
      template: existingSender?.template,
      headerParameters: existingSender?.headerParameters || [],
      bodyParameters: existingSender?.bodyParameters || [],
      footerParameters: existingSender?.footerParameters || [],
      headerMediaParam: existingSender?.headerMediaParam,
    });
  }

  return senders;
};

/**
 * Check if a single sender's message configuration is complete.
 *
 * @param sender - The sender to check
 * @param isFollowUp - Whether this is a follow-up sender (relaxed validation for some types)
 * @returns true if the sender has all required fields filled
 */
export const isSenderComplete = (sender: DraftCampaignSender, isFollowUp: boolean): boolean => {
  if (!sender.integrationId) {
    return false;
  }

  if (sender.integrationType === IntegrationType.WHATSAPP) {
    if (!sender.template?.id || sender.template.id.trim() === '') {
      return false;
    }
    const hasEmptyParam = (params?: Array<{ name: string }>) =>
      params?.some((p) => !p.name || p.name.toString().trim() === '') ?? false;
    if (
      hasEmptyParam(sender.headerParameters) ||
      hasEmptyParam(sender.bodyParameters) ||
      hasEmptyParam(sender.footerParameters)
    ) {
      return false;
    }
    return true;
  }

  if (sender.integrationType === IntegrationType.EMAIL) {
    // Follow-up email: only subject required (body is AI-generated)
    if (isFollowUp) {
      return !!(sender.subject && sender.subject.trim() !== '');
    }
    // Primary email: content + subject required
    return (
      !!(sender.content && sender.content.trim() !== '') &&
      !!(sender.subject && sender.subject.trim() !== '')
    );
  }

  // Voice, SMS, RCS — follow-ups are AI-generated, no user input needed
  if (isFollowUp) {
    return true;
  }
  // Primary: content required
  return !!(sender.content && sender.content.trim() !== '');
};

/**
 * Check if all sequential senders are complete.
 * Primary sender (index 0) uses full validation, follow-ups (index 1+) use relaxed rules.
 *
 * @param senders - Array of draft campaign senders
 * @returns true if every sender passes validation
 */
export const areAllSendersComplete = (senders: DraftCampaignSender[] | undefined): boolean => {
  if (!senders || senders.length === 0) {
    return false;
  }

  // Check primary (index 0)
  if (!isSenderComplete(senders[0], false)) {
    return false;
  }

  // Check follow-ups (index 1+)
  for (let i = 1; i < senders.length; i++) {
    if (!isSenderComplete(senders[i], true)) {
      return false;
    }
  }

  return true;
};

// ============================================================================
// UNMATCHED VARIABLE UTILITIES
// ============================================================================

/**
 * Get unmatched variables from current mappings
 * Returns all variables that are not yet mapped to any CSV column
 */
export const getUnmatchedVariables = (
  mappings: Record<string, ColumnMapping>,
  variables: VariableDefinition[]
): VariableDefinition[] => {
  const mappedTargets = new Set(
    Object.values(mappings)
      .map((m) => m.target)
      .filter(Boolean)
  );

  // conditionalGroup = "at least one required" (email/phone): once any member
  // is mapped, the rest are satisfied and not unmatched.
  const satisfiedGroups = new Set(
    variables
      .filter((v) => v.conditionalGroup && mappedTargets.has(v.name))
      .map((v) => v.conditionalGroup)
  );

  return variables.filter(
    (v) =>
      !mappedTargets.has(v.name) && !(v.conditionalGroup && satisfiedGroups.has(v.conditionalGroup))
  );
};

/** Blocks when unmatched: required, an unsatisfied contact group, or conditionally required. */
export const isRequiredUnmatchedVariable = (
  variable: VariableDefinition,
  extraRequired?: ReadonlySet<string>
): boolean =>
  variable.required || !!variable.conditionalGroup || !!extraRequired?.has(variable.name);

/** Single source of truth for "a required field is still unmatched" (gates + toasts). */
export const hasRequiredUnmatchedFields = (
  mappings: Record<string, ColumnMapping>,
  extraRequiredFields: string[] = []
): boolean => {
  const extra = new Set(extraRequiredFields);
  return getUnmatchedVariables(mappings, getDebtCampaignVariables()).some((v) =>
    isRequiredUnmatchedVariable(v, extra)
  );
};

/**
 * Check if the current pathname is a unified campaign route
 * (i.e. not a legacy type-specific /outreach/ or /payment-recovery/ route).
 */
export const isUnifiedCampaignRoute = (pathname: string): boolean =>
  !pathname.includes('/outreach/') && !pathname.includes('/payment-recovery/');
